import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/lib/site";

const SUCCESS = [
    "People feel seen, heard, and celebrated.",
    "Community members discover new people, places, and opportunities.",
    "Businesses and organizations feel proud to partner with the platform.",
    "Audiences feel more connected to the Twin Cities.",
    "Checking In with Courtnie Baby Johnson becomes a trusted voice for community storytelling.",
];

const VALUES = [
    "Modern",
    "Professional",
    "Welcoming",
    "Vibrant",
    "Community-centered",
    "Authentic",
    "Confident",
    "Optimistic",
];

export default function About() {
    const letterRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: letterRef,
        offset: ["start end", "end start"],
    });
    const yPhoto = useTransform(scrollYProgress, [0, 1], [0, -50]);

    return (
        <>
            <PageHeader
                eyebrow="About the brand"
                title="Hi, I'm Courtnie"
                scriptWord="thanks for checking in!"
                description="The story, the mission, and the vision behind Checking In with Courtnie Baby Johnson."
            />

            <section ref={letterRef} className="pb-24 sm:pb-32">
                <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-12">
                    <div className="lg:col-span-4">
                        <div className="lg:sticky lg:top-32">
                            <p className="eyebrow">A note from Courtnie</p>
                            <motion.div
                                style={{ y: yPhoto }}
                                className="photo-frame mt-8"
                            >
                                <img
                                    src={IMAGES.studio}
                                    alt="Inside the studio before a conversation"
                                    className="h-72 w-full object-cover sm:h-80"
                                />
                            </motion.div>
                            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-fog">
                                The studio — Twin Cities, MN
                            </p>
                        </div>
                    </div>

                    <div className="lg:col-span-8">
                        <Reveal>
                            <p className="text-xl leading-relaxed text-silver sm:text-2xl">
                                Hello, and welcome. I'm Courtnie Johnson, and
                                I'm so glad you're here.
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-8 leading-relaxed text-fog sm:text-lg">
                                Thank you for taking the time to learn more
                                about Checking In with Courtnie Baby Johnson
                                and the vision behind this platform. It was
                                built on one simple belief:{" "}
                                <span className="text-silver">
                                    every person has a story worth telling.
                                </span>
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-6 leading-relaxed text-fog sm:text-lg">
                                Every day throughout the Twin Cities, artists,
                                musicians, entrepreneurs, nonprofit
                                organizations, small business owners, community
                                leaders, and everyday neighbors are making a
                                meaningful impact. Many of those stories never
                                make the headlines, but they deserve to be
                                seen, heard, and celebrated.
                            </p>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <p className="mt-6 leading-relaxed text-fog sm:text-lg">
                                I believe communities become stronger when we
                                know the people around us. Every conversation
                                is an opportunity to introduce someone new,
                                celebrate meaningful work, and inspire others
                                to become more connected to the place they call
                                home. That belief is at the heart of everything
                                we create.
                            </p>
                        </Reveal>
                        <Reveal delay={0.25}>
                            <p className="mt-6 leading-relaxed text-fog sm:text-lg">
                                My hope is that this platform encourages
                                curiosity, builds relationships, and reminds us
                                that there is something extraordinary happening
                                all around us if we're willing to slow down,
                                ask questions, and truly listen.
                            </p>
                        </Reveal>
                        <Reveal delay={0.3}>
                            <p className="mt-6 leading-relaxed text-fog sm:text-lg">
                                Thank you for taking the time to check in. I'm
                                excited for what's ahead, and I hope you'll be
                                part of the journey as we continue discovering,
                                celebrating, and strengthening our
                                community—one conversation at a time.
                            </p>
                        </Reveal>
                        <Reveal delay={0.35}>
                            <p className="mt-8 font-script text-4xl text-neon-pink neon-pink-text">
                                — Courtnie Johnson
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="border-y border-white/10 bg-coal/40 py-24 sm:py-28">
                <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-2">
                    <div>
                        <Reveal>
                            <p className="eyebrow">The vision</p>
                            <h2 className="mt-4 font-display text-3xl font-bold uppercase sm:text-4xl">
                                Strengthening community through{" "}
                                <span className="font-script font-normal normal-case text-neon-pink neon-pink-text">
                                    authentic storytelling
                                </span>
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-6 leading-relaxed text-fog sm:text-lg">
                                The vision is to become the Twin Cities' trusted
                                community media platform — where people
                                discover inspiring stories, meaningful
                                conversations, and the people, places,
                                businesses, artists, and organizations that
                                make our community thrive.
                            </p>
                        </Reveal>
                        <Reveal delay={0.15}>
                            <p className="mt-6 leading-relaxed text-fog sm:text-lg">
                                As the platform grows, it will become more than
                                a media brand. It will serve as a trusted
                                community resource, a collaborative partner,
                                and a bridge that connects people through
                                conversation, creativity, and shared
                                experiences.
                            </p>
                        </Reveal>
                    </div>
                    <div>
                        <Reveal>
                            <p className="eyebrow">What success looks like</p>
                            <ol className="mt-6 space-y-0">
                                {SUCCESS.map((s, i) => (
                                    <li
                                        key={i}
                                        data-testid={`success-point-${i + 1}`}
                                        className="flex gap-5 border-t border-white/10 py-5 last:border-b"
                                    >
                                        <span className="font-mono text-xs text-neon-pink">
                                            0{i + 1}
                                        </span>
                                        <p className="leading-relaxed text-silver">
                                            {s}
                                        </p>
                                    </li>
                                ))}
                            </ol>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="py-24 sm:py-28">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow">How we show up</p>
                        <h2 className="mt-4 font-display text-3xl font-bold uppercase sm:text-4xl">
                            The energy behind everything
                        </h2>
                        <ul className="mt-10 flex flex-wrap gap-3" data-testid="about-values">
                            {VALUES.map((v) => (
                                <li
                                    key={v}
                                    className="border border-white/15 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-silver transition-colors hover:border-neon-pink hover:text-neon-pink"
                                >
                                    {v}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
